/**
 * Animated card avatars.
 *
 * The 8 SVGs live in ../assets/avatars and are pulled in as static
 * imports so webpack/CRA fingerprints + bundles them into the build
 * (no runtime remote fetch, no CORS surprises). At ~9 KB each × 8 ==
 * ~72 KB total, we don't bother lazy-loading them via dynamic imports;
 * the whole set fits in a single sprite-cache tick and the browser
 * caches the URLs across sessions anyway. Each SVG is self-animating
 * via SMIL / CSS keyframes so React never re-renders them mid-loop.
 *
 * Consumers: ``ChargerIcon`` (dashboard cards) and ``useShuffledAvatar``
 * (per-card assignment + one-minute reshuffle).
 */
import idleBlink from "@/assets/avatars/01-idle-blink.svg";
import sleep from "@/assets/avatars/02-sleep.svg";
import charging from "@/assets/avatars/03-charging.svg";
import fullHappy from "@/assets/avatars/04-full-happy.svg";
import patHappy from "@/assets/avatars/05-pat-happy.svg";
import dizzy from "@/assets/avatars/06-dizzy.svg";
import swing from "@/assets/avatars/07-baseball-swing.svg";
import overheat from "@/assets/avatars/08-overheat.svg";

export const AVATARS = [
  { key: "idle-blink", src: idleBlink, alt: "Blinking robot" },
  { key: "sleep", src: sleep, alt: "Sleeping robot" },
  { key: "charging", src: charging, alt: "Charging robot" },
  { key: "full-happy", src: fullHappy, alt: "Happy fully-charged robot" },
  { key: "pat-happy", src: patHappy, alt: "Robot enjoying a pat" },
  { key: "dizzy", src: dizzy, alt: "Dizzy robot" },
  { key: "swing", src: swing, alt: "Robot swinging a bat" },
  { key: "overheat", src: overheat, alt: "Overheating robot" },
];

/** Cheap 32-bit hash used ONLY to seed the initial assignment so
 *  the same link keeps the same avatar until the next reshuffle
 *  tick — no crypto strength needed. */
export function hashStringToInt(s) {
  let h = 2166136261 >>> 0; // FNV-1a offset basis
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Fisher–Yates on a copy. Uses Math.random() by default; caller can
 * pass a seeded RNG when they need determinism (initial assignment
 * uses a per-tab seed so the first paint is stable across strict-mode
 * double-renders).
 */
export function shuffle(arr, rng = Math.random) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Assign one avatar index (0..AVATARS.length-1) to each linkId such
 * that visible siblings never collide when the pool is big enough.
 *
 * Strategy:
 *   - If ``linkIds.length <= AVATARS.length`` we deal a permutation
 *     (guaranteed distinct).
 *   - Otherwise we deal the full permutation to the first N and cycle
 *     with a fresh shuffle for the rest so at worst two duplicates sit
 *     ``AVATARS.length`` cards apart.
 *   - When ``prev`` is passed AND we're on a reshuffle tick, we
 *     bias the result so no card keeps the SAME avatar as last tick
 *     (visible movement matters more than uniform randomness here —
 *     otherwise ~1/8 cards would look frozen at each minute mark).
 */
export function assignAvatars(linkIds, prev = null) {
  const n = linkIds.length;
  const pool = AVATARS.length;
  if (n === 0) return {};

  const out = {};
  let deck = shuffle([...Array(pool).keys()]);
  let cursor = 0;

  for (let i = 0; i < n; i += 1) {
    if (cursor >= deck.length) {
      // We ran out of unique cards — reshuffle for the overflow.
      deck = shuffle([...Array(pool).keys()]);
      cursor = 0;
    }
    let pick = deck[cursor];
    cursor += 1;
    const previous = prev?.[linkIds[i]];
    if (previous !== undefined && previous === pick && pool > 1) {
      // Swap with the next available card so this card visibly changes.
      const alt = deck[cursor] ?? deck[0];
      deck[cursor] = pick;
      pick = alt;
    }
    out[linkIds[i]] = pick;
  }
  return out;
}
